import { doc, getDoc, updateDoc, setDoc, deleteDoc, serverTimestamp, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../firebase";
import { ESTADOS } from "../../pedidos/services/estados";

// ─────────────────────────────────────────────
//  Estado override (el "ctrl+z" del admin)
// ─────────────────────────────────────────────

/**
 * Fuerza el estado de un pedido a cualquier valor válido,
 * saltando la validación de puedeTransicionar().
 * Deja un trail de auditoría en adminOverride.{estadoAnterior, motivo, porUid, at}.
 */
export async function adminForzarEstado(pedidoId, nuevoEstado, motivo, adminUid) {
  if (!nuevoEstado || !Object.values(ESTADOS).includes(nuevoEstado)) {
    throw new Error("Estado inválido: " + nuevoEstado);
  }
  if (!motivo?.trim()) {
    throw new Error("El motivo es obligatorio para registrar el cambio.");
  }

  const ref = doc(db, "pedidos", pedidoId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Pedido no encontrado.");

  const estadoAnterior = snap.data().estado || "PENDIENTE_ASIGNAR";

  const update = {
    estado: nuevoEstado,
    updatedAt: serverTimestamp(),
    [`timestamps.${nuevoEstado}`]: serverTimestamp(),
    adminOverride: {
      estadoAnterior,
      nuevoEstado,
      motivo: motivo.trim(),
      porUid: adminUid,
      at: serverTimestamp(),
    },
  };

  // Limpiar campos de error al salir de CON_ERROR
  if (estadoAnterior === "CON_ERROR" && nuevoEstado !== "CON_ERROR") {
    update.errorDetalle        = null;
    update.errorProductoCod    = null;
    update.errorProductoNombre = null;
    update.errorProductos      = null;
  }

  // Limpiar operario al volver a PENDIENTE_ASIGNAR
  if (nuevoEstado === "PENDIENTE_ASIGNAR") {
    update.operarioId     = null;
    update.operarioNombre = null;
  }

  await updateDoc(ref, update);
  return { estadoAnterior, nuevoEstado };
}

// ─────────────────────────────────────────────
//  Override TOTAL de estado (admin) — CUALQUIER estado, en cualquier momento,
//  incluso cruzando de colección (pedidos ↔ pedidos_despachados ↔ pedidos_entregados).
//  Ej: PENDIENTE_ASIGNAR → ENTREGADO, o ENTREGADO → DESPACHADO (pendiente de control).
//  Requiere que las reglas permitan escritura libre a admin en esas colecciones.
// ─────────────────────────────────────────────

// Estados que el admin puede setear (incluye los "pseudo-estado" por colección).
export const ADMIN_ESTADOS = [
  "PENDIENTE_ASIGNAR", "ASIGNADO", "EN_PREPARACION", "CON_ERROR",
  "ERROR_CONFIRMADO", "PREPARADO", "CONTROLADO", "DESPACHADO", "ENTREGADO", "CANCELADO",
];

// Colección donde vive cada estado.
function coleccionDeEstado(estado) {
  if (estado === "ENTREGADO") return "pedidos_entregados";
  if (estado === "DESPACHADO") return "pedidos_despachados";
  return "pedidos";
}

/**
 * Mueve un pedido a CUALQUIER estado, cruzando de colección si hace falta.
 * Lo busca en las tres colecciones, lo escribe en la que corresponde al nuevo
 * estado y borra el original si cambió de colección. Deja audit trail.
 */
export async function adminMoverAEstado(pedidoId, nuevoEstado, motivo, adminUid) {
  if (!ADMIN_ESTADOS.includes(nuevoEstado)) throw new Error("Estado inválido: " + nuevoEstado);
  if (!motivo?.trim()) throw new Error("El motivo es obligatorio para registrar el cambio.");

  const pid = String(pedidoId);
  const COLECCIONES = ["pedidos", "pedidos_despachados", "pedidos_entregados"];

  // 1) Localizar el pedido en cualquier colección.
  let srcColl = null, data = null;
  for (const c of COLECCIONES) {
    const s = await getDoc(doc(db, c, pid));
    if (s.exists()) { srcColl = c; data = s.data(); break; }
  }
  if (!data) throw new Error("Pedido no encontrado en ninguna colección.");

  const estadoAnterior = data.estado || "PENDIENTE_ASIGNAR";
  const dstColl = coleccionDeEstado(nuevoEstado);

  // 2) Campos a setear (además de conservar el resto del doc al mover).
  const cambios = {
    estado: nuevoEstado,
    updatedAt: serverTimestamp(),
    timestamps: { ...(data.timestamps || {}), [nuevoEstado]: serverTimestamp() },
    adminOverride: {
      estadoAnterior, nuevoEstado, motivo: motivo.trim(),
      porUid: adminUid || null, at: serverTimestamp(),
    },
  };

  // Limpiar error al salir de CON_ERROR.
  if (estadoAnterior === "CON_ERROR" && nuevoEstado !== "CON_ERROR") {
    Object.assign(cambios, {
      errorDetalle: null, errorProductoCod: null, errorProductoNombre: null,
      errorProductos: [], partesConError: {},
    });
  }
  // Volver a PENDIENTE_ASIGNAR → limpiar asignaciones.
  if (nuevoEstado === "PENDIENTE_ASIGNAR") {
    Object.assign(cambios, {
      operarioId: null, operarioNombre: null,
      asignaciones: {}, partesPreparadas: {}, partesConError: {}, responsablesUids: [],
    });
  }
  // DESPACHADO = "pendiente de control".
  if (nuevoEstado === "DESPACHADO") {
    cambios.necesitaControl = true;
    cambios.despachadoAt = data.despachadoAt || serverTimestamp();
    cambios.despachadoPor = data.despachadoPor || adminUid || null;
  }
  // ENTREGADO = control físico cerrado.
  if (nuevoEstado === "ENTREGADO") {
    cambios.necesitaControl = false;
    cambios.entregadoAt = data.entregadoAt || serverTimestamp();
    cambios.entregadoPor = data.entregadoPor || adminUid || null;
  }

  // 3) Escribir. Misma colección → update; distinta → crear en destino + borrar origen.
  if (srcColl === dstColl) {
    await updateDoc(doc(db, dstColl, pid), cambios);
  } else {
    await setDoc(doc(db, dstColl, pid), { ...data, ...cambios, movedAt: serverTimestamp(), source: "admin-override" });
    await deleteDoc(doc(db, srcColl, pid));
  }

  return { estadoAnterior, nuevoEstado, movido: srcColl !== dstColl, srcColl, dstColl };
}

// ─────────────────────────────────────────────
//  Configuración de sync Finnegans
// ─────────────────────────────────────────────

const SYNC_CONFIG_DEFAULT = { diasAtras: 7 };

export async function getSyncConfig() {
  try {
    const snap = await getDoc(doc(db, "config", "sync"));
    if (snap.exists()) return { ...SYNC_CONFIG_DEFAULT, ...snap.data() };
  } catch (e) {
    console.warn("[adminFS] No se pudo leer config/sync:", e.message);
  }
  return { ...SYNC_CONFIG_DEFAULT };
}

export async function saveSyncConfig(config) {
  await setDoc(doc(db, "config", "sync"), config, { merge: true });
}

// ─────────────────────────────────────────────
//  Helpers de consulta para el panel
// ─────────────────────────────────────────────

/** Lista de operarios (para reasignación) */
export async function listarOperarios() {
  try {
    const snap = await getDocs(query(collection(db, "users"), where("role", "==", "operario")));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn("[adminFS] listarOperarios:", e.message);
    return [];
  }
}
